import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import logo from './assets/logo.png';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import HomePage from './pages/HomePage';
import PlansPage from './pages/PlansPage';
import CreatePostPage from './pages/CreatePostPage';
import ProfilePage from './pages/ProfilePage';
import ConnectMetaPage from './pages/ConnectMetaPage';
import PostsPage from './pages/PostsPage';
import ResultsPage from './pages/ResultsPage';
import PaymentPage from './pages/PaymentPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import BillingPage from './pages/BillingPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import LoadingOverlay from './components/LoadingOverlay';
import './App.css';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingOverlay title="Caricamento..." subtitle="Un momento" />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AppLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  const tabs = [
    { path: '/',        icon: 'home',      label: 'Home'      },
    { path: '/posts',   icon: 'grid_on',   label: 'Post'      },
    { path: '/results', icon: 'bar_chart', label: 'Risultati' },
    { path: '/profile', icon: 'person',    label: 'Profilo'   },
  ];

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <div className="app-container">
      {/* Header fisso con logo — visibile su tutte le pagine */}
      <header className="app-header">
        <div className="app-header-inner">
          <img src={logo} alt="Molti Like" className="app-header-logo" />
        </div>
      </header>
      <div className="app-content">{children}</div>
      <nav className="bottom-nav">
        {tabs.map(t => (
          <Link key={t.path} to={t.path} className={`nav-tab${isActive(t.path) ? ' active' : ''}`}>
            <span className="nav-tab-icon mi mi-24">{t.icon}</span>
            <span className="nav-tab-label">{t.label}</span>
          </Link>
        ))}
        <Link
          to="/create-post"
          className={`nav-fab${location.pathname === '/create-post' ? ' active' : ''}`}
        >
          <span className="mi" style={{ fontSize: 28 }}>add</span>
        </Link>
      </nav>
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingOverlay title="Caricamento..." subtitle="Un momento" />;

  return (
    <Routes>
      <Route path="/login"           element={user ? <Navigate to="/" /> : <LoginPage />} />
      <Route path="/register"        element={user ? <Navigate to="/" /> : <RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password"  element={<ResetPasswordPage />} />
      <Route path="/"            element={<ProtectedRoute><AppLayout><HomePage /></AppLayout></ProtectedRoute>} />
      <Route path="/posts"       element={<ProtectedRoute><AppLayout><PostsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/plans"       element={<ProtectedRoute><AppLayout><PlansPage /></AppLayout></ProtectedRoute>} />
      <Route path="/results"     element={<ProtectedRoute><AppLayout><ResultsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/create-post" element={<ProtectedRoute><AppLayout><CreatePostPage /></AppLayout></ProtectedRoute>} />
      <Route path="/profile"     element={<ProtectedRoute><AppLayout><ProfilePage /></AppLayout></ProtectedRoute>} />
      <Route path="/connect"         element={<ProtectedRoute><AppLayout><ConnectMetaPage /></AppLayout></ProtectedRoute>} />
      <Route path="/payment"         element={<ProtectedRoute><AppLayout><PaymentPage /></AppLayout></ProtectedRoute>} />
      <Route path="/payment-success" element={<ProtectedRoute><AppLayout><PaymentSuccessPage /></AppLayout></ProtectedRoute>} />
      <Route path="/billing"         element={<ProtectedRoute><AppLayout><BillingPage /></AppLayout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
